import DesignTokens from '@tableau/design-tokens';
import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { ButtonType, DialogFooterButtonsWidget, TextAreaWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CloseAndDismiss } from '~src/commonInfra/dialogs/ReactModalService';
import { applyColor }from '~src/style/ColorService';

import { IpValidationUtil } from '../utils/IpValidationUtil';

export interface BulkEditDialogBodyProps {
    closeAndDismiss: CloseAndDismiss;
    allowedIPs: string[];
    onSave?: (allowedIPs: string[]) => Promise<void>;
}

export const IPALLOWLIST_BULK_EDIT_CANCEL_BTN_TEST_ID = 'ipallowlist-bulk-edit-cancel';
export const IPALLOWLIST_BULK_EDIT_SAVE_BTN_TEST_ID = 'ipallowlist-bulk-edit-save';

interface BulkEditDialogBodyState {
    textAreaValue: string;
    allowedIPs: string[];
    hasInvalidIPs: boolean;
    hasChanges: boolean;
}

export class BulkEditDialogBody extends React.Component<BulkEditDialogBodyProps, BulkEditDialogBodyState> {
    private textAreaRef = React.createRef<HTMLTextAreaElement>();

    constructor(props: BulkEditDialogBodyProps) {
        super(props);
        const initialIPs = props.allowedIPs.slice();
        const hasInvalidIPs = !this.validateIPs(initialIPs);
        this.state = {
            textAreaValue: props.allowedIPs.join('\n'),
            allowedIPs: initialIPs,
            hasInvalidIPs,
            hasChanges: false
        };
    }

    private getFooterContent = (): React.ReactNode => {
        return (
            <>
                <DialogFooterButtonsWidget
                    buttons={[
                        {
                            handleClick: () => {
                                this.props.closeAndDismiss.close();
                            },
                            buttonType: ButtonType.Outline,
                            children: Messages.cancel(),
                            testId: IPALLOWLIST_BULK_EDIT_CANCEL_BTN_TEST_ID
                        },
                        {
                            buttonType: ButtonType.Primary,
                            children: Messages.save(),
                            onClick: async () => {
                                if (this.props.onSave) {
                                    await this.props.onSave(this.state.allowedIPs);
                                }
                                this.props.closeAndDismiss.close();
                            },
                            testId: IPALLOWLIST_BULK_EDIT_SAVE_BTN_TEST_ID,
                            disabled: this.state.hasInvalidIPs || !this.state.hasChanges
                        }
                    ]}
                />
            </>
        );
    };

    private errorIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16}><path fill="currentColor" d="M6 0c3.32307692 0 6 2.67692308 6 6s-2.67692308 6-6 6-6-2.67692308-6-6 2.67692308-6 6-6zm3.80826837 2.78724354L2.78823013 9.80910485C3.66178215 10.54956 4.78584707 11 6 11c2.72727273 0 5-2.27272727 5-5 0-1.21461006-.4507793-2.33906427-1.19173163-3.21275646zM6 1C3.27272727 1 1 3.27272727 1 6c0 1.15838235.41000962 2.23476277 1.09071081 3.08982322l7.00012461-6.9983064C8.23561231 1.41033329 7.15883948 1 6 1z" /></svg>;


    private validateIPs = (ips: string[]): boolean => {
        return ips.every(ip => {
            const trimmedIp = ip.trim();
            return trimmedIp === '' || IpValidationUtil.validateIpAddress(trimmedIp);
        });
    };

    private checkForChanges = (currentIPs: string[]): boolean => {
        const originalIPs = this.props.allowedIPs.slice().sort();
        const sortedCurrentIPs = currentIPs.slice().sort();

        if (originalIPs.length !== sortedCurrentIPs.length) {
            return true;
        }

        return originalIPs.some((ip, index) => ip !== sortedCurrentIPs[index]);
    };

    private handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const lines = value.split('\n');
        const allowedIPs = lines.filter(ip => ip.trim() !== '');
        const hasInvalidIPs = !this.validateIPs(lines);
        const hasChanges = this.checkForChanges(allowedIPs);
        this.setState({ textAreaValue: value, allowedIPs, hasInvalidIPs, hasChanges });
    };

    public render(): JSX.Element {
        return <div>
            <div>
                <TextAreaWidget
                    ref={this.textAreaRef}
                    label={Messages.ipAllowlistAllowedIpAddresses()}
                    value={this.state.textAreaValue}
                    onChange={this.handleChange}
                    testId='ip-allowlist-bulk-edit-dialog-textarea'
                    style={{ height: 200, width: 564 }}
                    valid={!this.state.hasInvalidIPs}
                    key="ip-allowlist-textarea"
                />
                {!this.state.hasInvalidIPs ?
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationInfoOutlineIcon size={16} />
                    <div style={{ marginLeft: 3 }}>{Messages.ipAllowlistBulkEditDialogDescription()}</div>
                </div>
                :
                    <div style={{ display: 'flex', alignItems: 'center', color: applyColor(DesignTokens.color.font.default.error, 'red') }}>
                       {this.errorIcon()}
                       <div style={{ marginLeft: 3 }}>{Messages.ipAllowlistBulkEditDialogIvalid()}</div>
                    </div>
                }
                {this.state.hasInvalidIPs ?
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationInfoOutlineIcon size={16} />
                    <div style={{ marginLeft: 3 }}>{Messages.ipAllowlistBulkEditDialogDescription()}</div>
                </div>
                : '\u200B'
                }
            </div>
            <div style={{ marginTop: 30 }}>
                {this.getFooterContent()}
            </div>
        </div>;
    }
}